import type { OutreachDraftDto } from '@shared/outreach';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Mail, Send, Sparkles, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardBody, CardHeader } from '../../components/ui/card';
import { Alert, Badge } from '../../components/ui/feedback';
import { Input, Textarea } from '../../components/ui/form';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/session';

export interface DraftEditorProps {
  draft: OutreachDraftDto;
  onDeleted: () => void;
}

/**
 * Review, edit and send one draft.
 *
 * The facts the draft was built from sit beside it deliberately: an outreach email makes claims
 * about someone else's business, and the person sending it should be able to check every one
 * against the research before it goes out. Sending is a two-step action for the same reason.
 */
export function DraftEditor({ draft, onDeleted }: DraftEditorProps) {
  const client = useQueryClient();
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const [confirming, setConfirming] = useState(false);

  // No effect syncs these back from props: the page gives this component a `key` of the draft id,
  // so opening a different draft remounts it and the initial state above is simply correct. That
  // also means a save never clobbers what the user has typed.

  const sent = draft.status === 'sent';
  const dirty = subject !== draft.subject || body !== draft.body;

  const refresh = async (): Promise<void> => {
    await client.invalidateQueries({ queryKey: ['outreach'] });
  };

  const save = useMutation({
    mutationFn: () => api.patch<OutreachDraftDto>(`/api/outreach/${encodeURIComponent(draft.id)}`, { subject, body }),
    onSuccess: refresh,
  });

  const send = useMutation({
    mutationFn: () => api.post<OutreachDraftDto>(`/api/outreach/${encodeURIComponent(draft.id)}/send`, { confirm: true }),
    onSuccess: async () => {
      setConfirming(false);
      await refresh();
    },
  });

  const remove = useMutation({
    mutationFn: () => api.delete<{ ok: true }>(`/api/outreach/${encodeURIComponent(draft.id)}`),
    onSuccess: async () => {
      await refresh();
      onDeleted();
    },
  });

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            {draft.accountName ?? 'Business'}
            <Badge tone={sent ? 'success' : draft.status === 'failed' ? 'danger' : 'neutral'}>{draft.status}</Badge>
            <Badge tone={draft.writer === 'gemini' ? 'accent' : 'neutral'}>
              <Sparkles className="mr-0.5 h-3 w-3" aria-hidden="true" />
              {draft.writer === 'gemini' ? 'AI-written draft' : 'Built from the facts'}
            </Badge>
          </span>
        }
        description={
          <span className="flex items-center gap-1.5">
            <Mail className="h-3.5 w-3.5" aria-hidden="true" />
            To {draft.toName ? `${draft.toName} · ` : ''}
            <span className="font-medium text-foreground">{draft.toEmail}</span>
          </span>
        }
        action={
          !sent ? (
            <Button size="sm" variant="ghost" icon={<Trash2 className="h-3.5 w-3.5" />} onClick={() => remove.mutate()}>
              Discard
            </Button>
          ) : null
        }
      />
      <CardBody className="space-y-3">
        {send.error ? <Alert tone="error">{errorMessage(send.error)}</Alert> : null}
        {save.error ? <Alert tone="error">{errorMessage(save.error)}</Alert> : null}
        {draft.errorMessage && draft.status === 'failed' ? (
          <Alert tone="error" title="The last send failed">
            {draft.errorMessage}
          </Alert>
        ) : null}
        {sent ? (
          <Alert tone="success">
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
              Sent {draft.sentAt ? new Date(draft.sentAt).toLocaleString() : ''} — this record is now read-only.
            </span>
          </Alert>
        ) : null}

        <label className="block">
          <span className="mb-1 block text-[13px] font-medium text-foreground">Subject</span>
          <Input value={subject} readOnly={sent} onChange={(event) => setSubject(event.target.value)} />
        </label>

        <label className="block">
          <span className="mb-1 block text-[13px] font-medium text-foreground">Message</span>
          <Textarea
            className="min-h-64 font-sans text-[13px] leading-relaxed"
            value={body}
            readOnly={sent}
            onChange={(event) => setBody(event.target.value)}
          />
          <span className="mt-1 block text-[12px] text-muted-foreground">
            Plain text — 2.0 branding is applied when it is sent. Edit anything before sending.
          </span>
        </label>

        {draft.servicesPitched.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[13px] text-muted-foreground">Offering:</span>
            {draft.servicesPitched.map((service) => (
              <Badge key={service} tone="accent">
                {service}
              </Badge>
            ))}
          </div>
        ) : null}

        {/* The check on the whole feature: every claim traces to something collected. */}
        <details className="rounded-[var(--radius-panel)] border border-border px-3 py-2" open>
          <summary className="cursor-pointer text-[13px] font-medium text-foreground">
            What this was written from ({draft.basis.length} facts)
          </summary>
          <ul className="mt-2 space-y-0.5">
            {draft.basis.map((fact) => (
              <li key={fact} className="text-[13px] text-muted-foreground">
                · {fact}
              </li>
            ))}
          </ul>
          <p className="mt-2 flex items-start gap-1.5 text-[12px] text-warning">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
            Only these were given to the writer. Anything in the email beyond them is not from your research — check
            before sending.
          </p>
        </details>

        {!sent ? (
          <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
            <Button
              size="sm"
              variant="outline"
              loading={save.isPending}
              disabled={!dirty || save.isPending}
              onClick={() => save.mutate()}
            >
              {dirty ? 'Save changes' : 'Saved'}
            </Button>

            {confirming ? (
              <>
                <Button
                  size="sm"
                  icon={<Send className="h-3.5 w-3.5" />}
                  loading={send.isPending}
                  disabled={dirty}
                  onClick={() => send.mutate()}
                >
                  Yes — send to {draft.toEmail}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                icon={<Send className="h-3.5 w-3.5" />}
                disabled={dirty}
                onClick={() => setConfirming(true)}
              >
                Send…
              </Button>
            )}

            {dirty ? <span className="text-[12px] text-muted-foreground">Save your edits before sending.</span> : null}
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
